import type { RefObject } from "react"
import { useEffect, useState } from "react"

/**
 * Измерения, от которых зависит раскладка журнала.
 *
 * ПОЧЕМУ ШИРИНА МЕРЯЕТСЯ, А НЕ БЕРЁТСЯ ИЗ МЕДИАЗАПРОСА. Ширина колонки мастера — это
 * `floor(доступное / число мастеров)` с полом 150 и потолком 300. Доступное место зависит
 * не только от экрана: слева стоит навигация 200 px, которая у владельца сети сжимается
 * до 56. Медиазапрос про окно, а колонке нужна своя ширина, и меряется она у своего
 * контейнера.
 */
export function useElementWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const element = ref.current
    if (element === null) return

    setWidth(element.getBoundingClientRect().width)
    // Наблюдателя может не быть — в проверках под jsdom его нет вовсе. Тогда ширина
    // берётся один раз: сетка обязана отрисоваться и без слежения за размером.
    if (typeof ResizeObserver === "undefined") return

    // ResizeObserver, а не событие resize окна: контейнер меняет ширину и без изменения
    // окна — когда сворачивается навигация или появляется служебная полоса.
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry !== undefined) setWidth(entry.contentRect.width)
    })
    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [ref])

  return width
}

/**
 * Телефон.
 *
 * Порог 767 — тот же, что в оболочке: ниже него навигация уходит вниз вкладками.
 * Это НЕ точка, в которой сетка сжимается: сетка дня на телефоне не воспроизводится
 * вовсе, вместо неё список одного мастера (ТЗ §2.13). Поэтому решение принимает код
 * и переключает разные компоненты, а не CSS, который мог бы только перекрасить одну
 * и ту же сетку.
 */
export function useIsPhone(): boolean {
  const [isPhone, setIsPhone] = useState(() => matchPhone())

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return
    const query = window.matchMedia("(max-width: 767px)")
    const onChange = (): void => {
      setIsPhone(query.matches)
    }
    onChange()
    query.addEventListener("change", onChange)
    return () => {
      query.removeEventListener("change", onChange)
    }
  }, [])

  return isPhone
}

function matchPhone(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false
  return window.matchMedia("(max-width: 767px)").matches
}
